import { useContext, useMemo } from 'react';

import { useAtomValue } from 'jotai';
import { camelCase } from 'lodash';

import { ExternalPatientContext } from '@/app/external/constants';

import { isPediatricToothId } from '../../../../../../packages/utils/src/normalizeToothIds';
import type { ValidUniversalToothId } from '../../../../../../packages/utils/src/normalizeToothIds';
import type { SelectedCountsFilter } from '../../../../@types/appointments';
import type { PreferencesResponse } from '../../../../@types/preferences';
import { usePreferences } from '../../../../react-query/usePreferences';
import type {
  TProcedure,
  TProcedureStatus,
  TVisitSummary,
  TVisitTreatment,
} from '../../../../react-query/useVisitSummaries';
import { useUniversalToFDI } from '../../../../utils/internationalization/useUniversalToFDI';
import { mixedDentitionMap } from '../../../insuranceExport/constants';
import { selectedCountsFilterAtom } from '../../atoms';
import { useShouldHideHuddle } from '../../useShouldHideHuddle';

import { VisitSummariesStatusPriority } from './constants';
import { isSupportedVisualization } from './Tooth';
import type { TSupportedVisualizations, ToothStatus } from './Tooth';
import type { TDisplayOrderQuadrantAbbreviations } from './types';
import type { TProcedureWithAddressed } from './utils';
import {
  getIsEnabledByProcedureName,
  getIsRecommendationAddressedByTreatment,
  getMatchingPrimaryTooth,
  getRecommendedProcedures,
  hasPrimaryTeeth,
  isPermanentOnlyTooth,
} from './utils';

export const TOTAL_TEETH = 32;
const MINIMUM_AGE_FOR_ADULT_ODONTOGRAM = 14;
export const MAXIMUM_AGE_FOR_PEDIATRIC_ODONTOGRAM_NO_IMAGES = 6;

export const isUndetectedPediatricDentition = (
  age: number | null,
  hasImages: boolean,
) => {
  return (
    !hasImages && !!age && age <= MAXIMUM_AGE_FOR_PEDIATRIC_ODONTOGRAM_NO_IMAGES
  );
};

export const useToothStatuses = (
  visitSummary: TVisitSummary | undefined,
  selectedStatuses: TProcedureStatus[],
  age: number | null,
  hasImages: boolean,
): ToothStatus[] => {
  const { isIframe } = useContext(ExternalPatientContext);
  const isBasicOdontogram = useIsBasicOdontogram();
  const selectedCountsFilter = useAtomValue(selectedCountsFilterAtom);
  const { data: prefs } = usePreferences();

  return useMemo(() => {
    return Array.from({ length: TOTAL_TEETH }).map((_, index) => {
      const permanentToothId = index + 1;
      const permanentTooth = visitSummary?.teeth?.find(
        ({ toothId }) => toothId === permanentToothId.toString(),
      );
      const primaryTooth = getMatchingPrimaryTooth(
        String(permanentToothId) as ValidUniversalToothId,
        visitSummary,
      );
      const isDefaultAdultDentition =
        !!age && age >= MINIMUM_AGE_FOR_ADULT_ODONTOGRAM;

      let visualizationTooth = primaryTooth ?? permanentTooth;
      if (primaryTooth && permanentTooth && isDefaultAdultDentition) {
        // For patients >= 14 years old, if both the primary and permanent tooth
        // for a slot are present, then we should prefer information about the
        // permanent tooth.
        visualizationTooth = permanentTooth;
      }

      const priorityProcedure = getVisualizationProcedure(
        visualizationTooth?.procedures,
        selectedStatuses,
        selectedCountsFilter,
        prefs,
        visitSummary?.isPerioPatient,
        isBasicOdontogram,
      );

      if (isBasicOdontogram) {
        // In the case of the basic odontogram, the priority procedure (if there
        // is one) will always be a recommendation because we filter out
        // non-recommendations in getVisualizationProcedure.
        const hasRecommendation = priorityProcedure?.status === 'recommended';
        if (isIframe) {
          // TODO: In the future, we will want to show undetected teeth in the
          // iframe context to match VideaAI. For now, they should be solid
          // gray.
          return {
            type: 'normal',
            status: hasRecommendation ? 'recommended' : 'normal',
            toothId: String(permanentToothId) as ValidUniversalToothId,
          };
        }

        return {
          // In the basic odontogram, if a tooth is undetected in images, it
          // should appear as undetected.
          type:
            visualizationTooth && !!visualizationTooth.hasSummary
              ? 'normal'
              : 'missing',
          status: hasRecommendation ? 'recommended' : 'normal',
          toothId: String(permanentToothId) as ValidUniversalToothId,
        };
      }

      const priorityStatus = priorityProcedure?.status;
      let priorityType: TSupportedVisualizations | undefined = 'normal';
      const isUnsupportedVisualization =
        priorityProcedure?.name === 'other' && priorityStatus !== 'recommended';

      if (!visualizationTooth) {
        // TODO: In the future, we will want to show undetected teeth in the
        // iframe context to match the VideaAI. For now, they should be solid
        // gray.
        priorityType = isIframe ? 'normal' : 'missing';
      } else if (isSupportedVisualization(priorityProcedure?.name)) {
        priorityType = priorityProcedure?.name;
      } else if (isUnsupportedVisualization) {
        const statusKey = camelCase(
          priorityStatus,
        ) as keyof TProcedure['treatments'];
        const treatments = priorityProcedure.treatments[statusKey];

        const supportedVisualization = treatments.find(
          ({ txGroupVisualization }) =>
            txGroupVisualization &&
            isSupportedVisualization(txGroupVisualization),
        )?.txGroupVisualization;

        priorityType = supportedVisualization as TSupportedVisualizations;
        // Default to unknown if the procedure name is not supported and if showUnknownViz is true
        if (prefs?.showUnknownViz) {
          priorityType = priorityType ?? 'unknown';
        }
      } else if (visualizationTooth?.isMissing) {
        // There might be a procedure with a supported visualization for a tooth
        // that `isMissing`, so we want to execute this condition last.
        priorityType = 'extracted';
      } else if (!visualizationTooth.hasSummary) {
        // If the tooth is in the visit summary, doesn't have PMS treatments
        // (see conditions above), and doesn't have a CAIS summary, it's likely
        // only present in a dental photo, in which case we shouldn't show it as
        // normal because users will assume that visual means it's been detected
        // in an x-ray.
        // TODO: In the future, we will want to show undetected teeth in the
        // iframe context to match VideaAI. For now, they should be solid gray.
        priorityType = isIframe ? 'normal' : 'missing';
      }

      const shouldDefaultToPrimaryTooth =
        (hasPrimaryTeeth(visitSummary) && !isDefaultAdultDentition) ||
        isUndetectedPediatricDentition(age, hasImages);

      let toothId = visualizationTooth?.toothId ?? String(permanentToothId);
      if (
        !visualizationTooth &&
        !isPermanentOnlyTooth(toothId) &&
        shouldDefaultToPrimaryTooth
      ) {
        // For patients < 14 years old with some number of primary teeth, let's
        // default to primary SVGs for undetected teeth. For patients <= 6 years
        // old without any images, let's default to primary SVGs for undetected
        // teeth.
        const matchingEntry = mixedDentitionMap.find(
          (tooth) => tooth.permanentTooth === toothId,
        );
        if (matchingEntry) {
          toothId = matchingEntry.primaryTooth;
        }
      }

      return {
        toothId: toothId as ValidUniversalToothId,
        type: priorityType ?? 'normal',
        status: priorityStatus,
      };
    });
  }, [
    age,
    hasImages,
    isBasicOdontogram,
    isIframe,
    prefs,
    selectedCountsFilter,
    selectedStatuses,
    visitSummary,
  ]);
};

export interface VisitTreatmentWithCount extends TVisitTreatment {
  count?: number;
}

interface MouthAreaTreatments {
  mouthArea: string;
  treatments: VisitTreatmentWithCount[];
}

// First, we filter for mouth areas that have any treatment aligning with the
// particular status. Then we filter those treatments for ones aligning with the
// particular status. Finally, we de-dupe the remaining treatments according to
// treatment code.
export const useTreatmentsGroupedByStatus = (
  visitSummary?: TVisitSummary,
): Omit<
  Record<TProcedureStatus, MouthAreaTreatments[]>,
  'recommended' | 'normal'
> => {
  const { displayToothNumber } = useUniversalToFDI();

  const filterMouthAreaAndTreatmentsByStatus = (
    status: TProcedureStatus,
    mouthAreaTreatments: MouthAreaTreatments[],
  ) => {
    return mouthAreaTreatments
      .map(({ mouthArea, treatments }) => ({
        mouthArea,
        treatments: treatments.filter(
          (treatment) => treatment.status === status,
        ),
      }))
      .filter(({ treatments }) => !!treatments.length);
  };

  const aggregateDuplicateTreatments = (treatments?: TVisitTreatment[]) => {
    if (!treatments) return [];

    // Group treatments by txCode and count duplicates
    const treatmentMap = new Map<string, VisitTreatmentWithCount>();

    treatments.forEach((treatment) => {
      const existing = treatmentMap.get(treatment.txCode);
      if (existing) {
        existing.count = (existing.count ?? 0) + 1;
      } else {
        treatmentMap.set(treatment.txCode, { ...treatment, count: 1 });
      }
    });

    return Array.from(treatmentMap.values());
  };

  const { displayLocalizedQuadrantShortName: quadrantInLocalization } =
    useUniversalToFDI();

  return useMemo(() => {
    const treatmentsByTooth =
      visitSummary?.teeth.map((tooth) => ({
        mouthArea: displayToothNumber(tooth.toothId),
        treatments: tooth.treatments ?? [],
      })) ?? [];
    const treatmentsByQuadrant =
      visitSummary?.quadrants.map((quadrant) => ({
        mouthArea: quadrantInLocalization(quadrant.quadrant),
        treatments: quadrant.treatments ?? [],
      })) ?? [];
    const treatmentsByArch =
      visitSummary?.arches.map((arch) => ({
        mouthArea: arch.arch === 'mandibular' ? 'Lower Arch' : 'Upper Arch',
        treatments: arch.treatments ?? [],
      })) ?? [];
    const mouthTreatments = {
      mouthArea: 'Whole Dentition',
      treatments: visitSummary?.mouth.treatments ?? [],
    };

    const mouthAreaTreatments = [
      ...treatmentsByTooth,
      ...treatmentsByQuadrant,
      ...treatmentsByArch,
      mouthTreatments,
    ];

    const filterAndAggregateTreatments = (
      status: TProcedureStatus,
      mouthAreaTx: MouthAreaTreatments[],
    ) => {
      const filtered = filterMouthAreaAndTreatmentsByStatus(
        status,
        mouthAreaTx,
      );
      return filtered.map(({ mouthArea, treatments }) => ({
        mouthArea,
        treatments: aggregateDuplicateTreatments(treatments),
      }));
    };

    return {
      unscheduled: filterAndAggregateTreatments(
        'unscheduled',
        mouthAreaTreatments,
      ),
      scheduled_today: filterAndAggregateTreatments(
        'scheduled_today',
        mouthAreaTreatments,
      ),
      scheduled_future: filterAndAggregateTreatments(
        'scheduled_future',
        mouthAreaTreatments,
      ),
      completed: filterAndAggregateTreatments('completed', mouthAreaTreatments),
    };
  }, [
    displayToothNumber,
    visitSummary?.arches,
    visitSummary?.mouth.treatments,
    visitSummary?.quadrants,
    visitSummary?.teeth,
    quadrantInLocalization,
  ]);
};

// This hook is used to determine the highest priority treatment status to
// display in tooth and quadrant hover cards, considering both procedures and
// treatments.
export const useGetTreatmentStatus = (
  procedures?: TProcedure[],
  treatments?: TVisitTreatment[],
  preferences?: PreferencesResponse,
): TProcedureStatus | undefined => {
  return useMemo(() => {
    const hasRecommendedProcedure = !!procedures?.some(
      ({ name, recommended }) => {
        const isPreferenceEnabled = getIsEnabledByProcedureName(
          name,
          preferences,
        );
        return recommended && isPreferenceEnabled;
      },
    );

    const combinedStatuses = treatments?.map(({ status }) => status) ?? [];
    if (hasRecommendedProcedure) {
      combinedStatuses.push('recommended');
    }

    return combinedStatuses.sort(
      (a, b) =>
        VisitSummariesStatusPriority[a] - VisitSummariesStatusPriority[b],
    )[0];
  }, [preferences, procedures, treatments]);
};

// This function is used to determine the highest priority procedure to
// visualize for a tooth/quadrant/arch/mouth, considering both procedures and
// nested treatments. It is a pure function because `useToothStatuses` cannot
// use a hook.
export const getVisualizationProcedure = (
  procedures?: TProcedure[],
  selectedStatuses?: TProcedureStatus[],
  selectedCountsFilter?: SelectedCountsFilter,
  preferences?: PreferencesResponse,
  isPerioPatient?: boolean,
  isBasicOdontogram?: boolean,
) => {
  // Aggregate treatments from all procedures because a treatment under a
  // different procedure group (feature) might address the recommendation
  const allTreatments = procedures?.flatMap((procedure) =>
    Object.values(procedure.treatments).flat(),
  );

  const proceduresFiltered = procedures?.reduce((acc, procedure) => {
    const {
      name,
      status,
      treatments: procedureTreatments,
      recommended: isRecommended,
    } = procedure;
    const isPreferenceEnabled = getIsEnabledByProcedureName(name, preferences);

    const isOpportunityStatusSelected =
      selectedStatuses?.includes('recommended');
    // Both of these conditions may be true for an unscheduled opportunity
    // (e.g., `status` === "unscheduled" and `recommended` === true), in which
    // case we want to include two distinct procedures in the list.
    if (
      selectedStatuses?.includes(status) &&
      status !== 'recommended' &&
      !isBasicOdontogram
    ) {
      acc.unshift(procedure);
    }
    if (
      isRecommended &&
      isPreferenceEnabled &&
      (isOpportunityStatusSelected || isBasicOdontogram)
    ) {
      const isRecommendationAddressedByTreatment =
        getIsRecommendationAddressedByTreatment(
          procedure,
          allTreatments,
          isPerioPatient,
        );
      if (!isRecommendationAddressedByTreatment) {
        acc.unshift({ ...procedure, status: 'recommended' });
      }
    }

    const nestedTreatments = Object.values(procedureTreatments).filter(
      (statusTreatments) => {
        const isNotTopLevelStatus =
          procedure.status !== statusTreatments[0]?.status;
        return (
          !!statusTreatments.length &&
          selectedStatuses?.includes(statusTreatments[0].status) &&
          isNotTopLevelStatus
        );
      },
    );
    const nestedStatuses = nestedTreatments.map(
      (statusTreatments) => statusTreatments[0].status,
    );

    nestedStatuses.forEach((nestedStatus) => {
      acc.unshift({
        ...procedure,
        status: nestedStatus,
        priority: 60, // De-prioritize the nested procedures
      });
    });

    return acc;
  }, [] as TProcedure[]);

  // If a recommendation filter is selected, then sort opportunities to the
  // front of the list. Otherwise, sort by priority and status.
  const proceduresPrioritized = proceduresFiltered?.sort((a, b) => {
    // At this point, all unscheduled opportunities have been converted to a
    // status of "recommended".
    const isFilterMatchA =
      selectedCountsFilter === 'perio'
        ? a.name === 'perio' || a.name === 'locPerio'
        : selectedCountsFilter === a.name;
    const isFilterMatchB =
      selectedCountsFilter === 'perio'
        ? b.name === 'perio' || b.name === 'locPerio'
        : selectedCountsFilter === b.name;
    const isOpportunityFilterA =
      a.status === 'recommended' &&
      (selectedCountsFilter === 'ALL_OPPORTUNITIES' || isFilterMatchA);
    const isOpportunityFilterB =
      b.status === 'recommended' &&
      (selectedCountsFilter === 'ALL_OPPORTUNITIES' || isFilterMatchB);

    if (isOpportunityFilterA && !isOpportunityFilterB) {
      return -1;
    }
    if (!isOpportunityFilterA && isOpportunityFilterB) {
      return 1;
    }
    if (a.priority === b.priority) {
      return (
        VisitSummariesStatusPriority[a.status] -
        VisitSummariesStatusPriority[b.status]
      );
    }
    return a.priority - b.priority;
  });

  return proceduresPrioritized?.[0];
};

export const useVisualizationProcedure = (
  procedures?: TProcedure[],
  selectedStatuses?: TProcedureStatus[],
  isPerioPatient?: boolean,
) => {
  const isBasicOdontogram = useIsBasicOdontogram();
  const { data: prefs } = usePreferences();
  const selectedCountsFilter = useAtomValue(selectedCountsFilterAtom);

  return useMemo(
    () =>
      getVisualizationProcedure(
        procedures,
        selectedStatuses,
        selectedCountsFilter,
        prefs,
        isPerioPatient,
        isBasicOdontogram,
      ),
    [
      procedures,
      selectedStatuses,
      selectedCountsFilter,
      prefs,
      isPerioPatient,
      isBasicOdontogram,
    ],
  );
};

export interface MouthAreaVisualization {
  type?: TProcedure['name'];
  status?: TProcedureStatus;
}

export const useRecommendedProceduresGroupedByMouthArea = (
  visitSummary?: TVisitSummary,
  shouldExcludeAddressedOpportunities?: boolean,
): Partial<Record<string, TProcedureWithAddressed[]>> => {
  const { data: prefs } = usePreferences();
  const { displayToothNumber, displayLocalizedQuadrantShortName } =
    useUniversalToFDI();

  return useMemo(() => {
    const grouped: Partial<Record<string, TProcedureWithAddressed[]>> = {};

    if (!visitSummary) return grouped;

    const mapRecommendedProcedures = (
      data:
        | TVisitSummary['teeth' | 'quadrants' | 'arches'][number]
        | TVisitSummary['mouth'],
      identifier: string,
    ) => {
      const recommendedProcedures = getRecommendedProcedures(
        data,
        prefs,
        shouldExcludeAddressedOpportunities,
        visitSummary?.isPerioPatient,
      );
      if (!recommendedProcedures.length) return;

      if (!grouped[identifier]) {
        grouped[identifier] = [];
      }
      recommendedProcedures.forEach((procedure) => {
        (grouped[identifier] as TProcedureWithAddressed[]).push(procedure);
      });
    };

    visitSummary.teeth.forEach((tooth) => {
      const matchingPrimaryTooth = getMatchingPrimaryTooth(
        tooth.toothId as ValidUniversalToothId,
        visitSummary,
      );
      // Don't include recommendations on permanent teeth if there is a
      // corresponding primary tooth
      if (isPediatricToothId(tooth.toothId) || !matchingPrimaryTooth) {
        mapRecommendedProcedures(
          tooth,
          displayToothNumber(tooth.toothId) as ValidUniversalToothId,
        );
      }
    });
    visitSummary.quadrants.forEach((quadrant) => {
      mapRecommendedProcedures(
        quadrant,
        displayLocalizedQuadrantShortName(quadrant.quadrant),
      );
    });
    visitSummary.arches.forEach((arch) => {
      mapRecommendedProcedures(
        arch,
        arch.arch === 'mandibular' ? 'Lower Arch' : 'Upper Arch',
      );
    });
    mapRecommendedProcedures(visitSummary.mouth, 'Whole Dentition');

    return grouped;
  }, [
    displayLocalizedQuadrantShortName,
    displayToothNumber,
    prefs,
    shouldExcludeAddressedOpportunities,
    visitSummary,
  ]);
};

export type MouthArea =
  | ValidUniversalToothId
  | TDisplayOrderQuadrantAbbreviations
  | TVisitSummary['arches'][number]['arch']
  | 'mouth';

interface MouthAreaWithIsAddressed {
  mouthArea: MouthArea;
  isAddressed?: boolean;
}

export const useMouthAreasGroupedByRecommendedProcedure = (
  visitSummary?: TVisitSummary,
) => {
  const { data: prefs } = usePreferences();

  return useMemo(() => {
    const grouped: Record<TProcedure['name'], MouthAreaWithIsAddressed[]> = {
      crown: [],
      enamelRegen: [],
      extraction: [],
      filling: [],
      implant: [],
      aligner: [],
      other: [],
      perio: [],
      locPerio: [],
      rootCanal: [],
    };

    if (!visitSummary) return grouped;

    const mapRecommendedProcedures = (
      data:
        | TVisitSummary['teeth' | 'quadrants' | 'arches'][number]
        | TVisitSummary['mouth'],
      identifier: MouthArea,
    ) => {
      const recommendedProcedures = getRecommendedProcedures(
        data,
        prefs,
        undefined,
        visitSummary?.isPerioPatient,
      );
      recommendedProcedures.forEach(({ isAddressed, name }) => {
        if (!grouped[name]) {
          grouped[name] = [];
        }
        grouped[name].push({ mouthArea: identifier, isAddressed });
      });
    };

    visitSummary.teeth.forEach((tooth) => {
      const matchingPrimaryTooth = getMatchingPrimaryTooth(
        tooth.toothId as ValidUniversalToothId,
        visitSummary,
      );
      // Don't include recommendations on permanent teeth if there is a
      // corresponding primary tooth
      if (isPediatricToothId(tooth.toothId) || !matchingPrimaryTooth) {
        mapRecommendedProcedures(tooth, tooth.toothId as ValidUniversalToothId);
      }
    });
    visitSummary.quadrants.forEach((quadrant) => {
      mapRecommendedProcedures(quadrant, quadrant.quadrant);
    });
    visitSummary.arches.forEach((arch) => {
      mapRecommendedProcedures(arch, arch.arch);
    });
    mapRecommendedProcedures(visitSummary.mouth, 'mouth');

    return grouped;
  }, [prefs, visitSummary]);
};

/**
 * In the basic odontogram, recommendations are limited to basic categories
 * (restorative, incipient, and periodontal), and teeth with recommendations are
 * filled with solid purple. No PMS treatments are shown.
 *
 * When huddle is enabled or forceEnableRecommendations is true, we show full
 * detailed recommendations and PMS treatments.
 */
export const useIsBasicOdontogram = () => {
  const hideHuddle = useShouldHideHuddle();
  const { data: prefs } = usePreferences();
  return hideHuddle && !prefs?.forceEnableRecommendations;
};
